// @flow
import React, {useState} from "react";
import {Modal, Form, Button, message, Spin, Upload} from "antd";
import {useTranslation} from "react-i18next";
import {UploadOutlined} from "@ant-design/icons";
import {useFormik} from "formik";
import * as Yup from "yup";
import {useStore} from "hooks/useStore";
import {useFetch} from "hooks/useFetch";
import Icon from "@ant-design/icons";
import {HiDocumentArrowUp} from "react-icons/hi2";

export const UploadCourierEquipmentModal = ({
    onCancel = () => {},
    onSuccess = () => {},
    courierId,
    disabled = false
}) => {
    const stores = useStore();
    const {t} = useTranslation(["courierContracts"]);
    const [loading, setLoading] = useState(false);
    const [visible, setVisible] = useState(false);
    const [loadingTemplates, templates] = useFetch({
        fetchFn: stores.contractStore.getCourierContractTemplates.bind(null, {
            courierId
        }),
        dependencies: [courierId, visible],
        condition: visible,
        initialCall: true,
        defaultData: []
    });

    const uploadFormik = useFormik({
        initialValues: {
            selectedFiles: [],
            selectedTemplateId:
                templates?.data?.filter(
                    template => template.contractName === "courierEquipmentAgreement"
                )?.[0]?._id || null
        },
        enableReinitialize: true,
        validationSchema: Yup.object({
            selectedFiles: Yup.array()
                .min(1, "Lütfen en az bir dosya seçin!")
                .max(3, "Maksimum 3 dosya yüklenebilir!"),
            selectedTemplateId: Yup.string()
                .nullable()
                .required("Lütfen bir şablon seçin!")
        }),
        onSubmit: async values => {
            try {
                setLoading(true);

                const response = await stores.contractStore.uploadCourierContract({
                    courierId,
                    templateId: values.selectedTemplateId,
                    files: values.selectedFiles
                });

                if (response.data && response.success) {
                    message.success(t("messages.contractUploaded"));
                    uploadFormik.resetForm();
                    setVisible(false);
                    onSuccess();
                } else {
                    message.error(response.data?.message || "Sözleşme yüklenirken hata oluştu");
                }
            } catch (error) {
                console.error("Contract upload error:", error);
                message.error(error.message || "Sözleşme yüklenirken hata oluştu");
            } finally {
                setLoading(false);
            }
        }
    });

    const handleCancel = () => {
        uploadFormik.resetForm();
        setVisible(false);
        uploadFormik.resetForm();
        onCancel?.();
    };

    return (
        <>
            <Button
                type="text"
                onClick={() => setVisible(true)}
                disabled={disabled}
                size="small"
                style={{padding: "0 2px"}}
            >
                <Icon component={HiDocumentArrowUp} style={{fontSize: 24}} />
            </Button>
            <Modal
                title={t("modal.uploadContract")}
                visible={visible}
                onCancel={handleCancel}
                width={600}
                footer={[
                    <Button key="cancel" onClick={handleCancel}>
                        {t("modal.cancel")}
                    </Button>,
                    <Button
                        key="upload"
                        type="primary"
                        loading={loading}
                        onClick={uploadFormik.handleSubmit}
                        disabled={
                            !uploadFormik.values.selectedFiles.length ||
                            !uploadFormik.values.selectedTemplateId
                        }
                    >
                        {t("modal.upload")}
                    </Button>
                ]}
            >
                <Spin spinning={loadingTemplates}>
                    <Form layout="vertical">
                    <Form.Item
                        label={t("modal.selectFiles")}
                        required
                        validateStatus={
                            uploadFormik.touched.selectedFiles &&
                            uploadFormik.errors.selectedFiles
                                ? "error"
                                : ""
                        }
                        help={
                            uploadFormik.touched.selectedFiles &&
                            uploadFormik.errors.selectedFiles
                        }
                    >
                        <Upload
                            multiple
                            beforeUpload={(file, fileList) => {
                                const current = uploadFormik.values.selectedFiles;
                                const remainingSlots = 3 - current.length;
                                if (remainingSlots <= 0) {
                                    message.warning("Maksimum 3 dosya yüklenebilir!");
                                    return false;
                                }
                                if (file.uid !== fileList[fileList.length - 1].uid) {
                                    return false;
                                }
                                const toAdd = fileList.slice(0, remainingSlots);
                                if (fileList.length > remainingSlots) {
                                    message.warning("Maksimum 3 dosya yüklenebilir!");
                                }
                                uploadFormik.setFieldValue("selectedFiles", [
                                    ...current,
                                    ...toAdd
                                ]);
                                return false;
                            }}
                            maxCount={3}
                            fileList={uploadFormik.values.selectedFiles}
                            onRemove={file => {
                                const updated = uploadFormik.values.selectedFiles.filter(
                                    f => f.uid !== file.uid
                                );
                                uploadFormik.setFieldValue("selectedFiles", updated);
                            }}
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        >
                            <Button
                                icon={<UploadOutlined />}
                                disabled={uploadFormik.values.selectedFiles.length >= 3}
                            >
                                {t("modal.selectFiles")}
                            </Button>
                        </Upload>
                    </Form.Item>
                    </Form>
                </Spin>
            </Modal>
        </>
    );
};
